import React from "react"
import {
    Box,
    HorizontalStack,
    SIZES,
    Text,
    componentStyles,
    useColors,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBan } from "@fortawesome/free-solid-svg-icons/faBan"
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck"
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons/faCircleNotch"
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"

import {
    TOOL_CALL_VARIANTS,
    type ToolCallVariant,
} from "~components/shared/chat/chat-message-tool-call-helpers"
import { TEST_IDS } from "~config/constants"

const ICON_BY_VARIANT: Record<ToolCallVariant, IconDefinition> = {
    [TOOL_CALL_VARIANTS.RUNNING]: faCircleNotch,
    [TOOL_CALL_VARIANTS.DONE]: faCheck,
    [TOOL_CALL_VARIANTS.ERROR]: faTriangleExclamation,
    [TOOL_CALL_VARIANTS.DENIED]: faBan,
}

interface Props {
    readonly variant: ToolCallVariant
    readonly label: string
    readonly errorText: string | undefined
}

export default function ChatMessageToolCallChip({
    variant,
    label,
    errorText,
}: Props) {
    const isErrorLike =
        variant === TOOL_CALL_VARIANTS.ERROR ||
        variant === TOOL_CALL_VARIANTS.DENIED
    const isSpinning = variant === TOOL_CALL_VARIANTS.RUNNING
    const styles = useStyles({ isErrorLike })

    return (
        <Box style={styles.root} testId={TEST_IDS.CHAT_MESSAGE_TOOL_CALL}>
            <HorizontalStack style={styles.header}>
                <FontAwesomeIcon
                    icon={ICON_BY_VARIANT[variant]}
                    spin={isSpinning}
                />
                <Text style={styles.label}>{label}</Text>
            </HorizontalStack>
            {errorText && (
                <Text
                    style={styles.errorText}
                    testId={TEST_IDS.CHAT_MESSAGE_TOOL_CALL_ERROR}
                >
                    {errorText}
                </Text>
            )}
        </Box>
    )
}

function useStyles({ isErrorLike }: { isErrorLike: boolean }) {
    const colors = useColors()

    return componentStyles({
        root: {
            borderRadius: "md",
            paddingX: 3,
            paddingY: 2,
            backgroundColor: isErrorLike ? colors.bgSecondary : "transparent",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: colors.border,
            fontSize: SIZES.SM,
            opacity: isErrorLike ? 1 : 0.75,
        },
        header: { alignItems: "center", gap: 2 },
        label: { fontSize: SIZES.SM, fontWeight: "medium" },
        errorText: {
            fontSize: SIZES.SM,
            color: colors.secondaryText,
            marginTop: 1,
        },
    })
}
